import {
  Grid,
  GridItem,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  HStack,
  Button,
  Icon,
  Box,
  Center,
  Checkbox,
  Text,
  VStack,
  FormErrorMessage,
  useToast,
} from '@chakra-ui/react'
import { useProjectTypeSelectOptions } from 'api/pc-projects'
import { useProperty } from 'api/property'
import { useTrades } from 'api/vendor-details'
import Select from 'components/form/react-select'
import React, { useEffect, useState } from 'react'
import { Controller, useFieldArray, useFormContext } from 'react-hook-form'
import { BiPlus } from 'react-icons/bi'
import { MdOutlineCancel } from 'react-icons/md'
import NumberFormat from 'react-number-format'
import { calendarIcon } from 'theme/common-style'
import { SelectOption } from 'types/transaction.type'
import { isValidAndNonEmpty } from 'utils'
import { useAuth, useClient } from 'utils/auth-context'
import { useUserRolesSelector } from 'utils/redux-common-selectors'
import { TicketStatusOptions, TicketTimeSlots } from './order-intake-form-edit'
import { useQuery } from 'react-query'
import { useTicketWorkOrders } from 'api/ticket'
import { useTransactionsV1 } from 'api/transactions'
import { STATUS_CODE } from 'features/common/status'
import { NEW_PROJECT } from 'features/vendor/projects/projects.i18n'
import { t } from 'i18next'
import { NumberInput } from 'components/input/input'
import { useProjectOverrideStatusSelectOptions } from './hooks'

export const PriorityOptions: SelectOption[] = [
  { value: 'emergency', label: 'Emergency' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
]

export const ContactAttemptOptions = [
  { value: 1, label: 'Not Answered' },
  { value: 2, label: 'Answered' },
  { value: 3, label: 'Disconnected ' },
  { value: 4, label: 'Wrong Number' },
]
type AutoTicketId = {
  ticketId: string
}
const useTicketId = (propertyId: any, isEdit: boolean) => {
  const client = useClient()
  return useQuery<AutoTicketId>(
    ['GetTicketId', propertyId, isEdit],
    async () => {
      const response = await client(`projects/ticket/id?propertyId=${propertyId}`, {})
      return response?.data
    },
    {
      enabled: !!propertyId && !isEdit,
      cacheTime: 0,
    },
  )
}

type Props = {
  maintenanceCoordinatorSelectOptions: SelectOption[]
  maintenanceSpecialistSelectOptions: SelectOption[]
  vendorTrades?: any
  changeTab?: any
  propertyId?: any
  isReceivable?: boolean | null
  isNotesRequired?: any
  setIsNotesRequired: any
  isProjectCancelled?: boolean
  isLoading?: boolean
  projectData?: any
}

export const OrderIntakeForm: React.FC<Props> = ({
  maintenanceCoordinatorSelectOptions,
  maintenanceSpecialistSelectOptions,
  vendorTrades,
  propertyId,
  isReceivable,
  isNotesRequired,
  setIsNotesRequired,
  isProjectCancelled,
  isLoading,
  projectData,
}: Props): JSX.Element => {
  const {
    register,
    control,
    formState: { errors },
    watch,
    getValues,
    setValue,
    setError,
    clearErrors,
  } = useFormContext()

  const formValues = watch()
  const isEdit = !!formValues?.id
  const { fields: scheduleFields } = useFieldArray({ control, name: 'schedule' })
  const { propertyData } = useProperty(propertyId)
  const toast = useToast()
  const [hasInvoiceCheck, setHasInvoiceCheck] = useState<boolean>(false)

  const { transactions } = useTransactionsV1(projectData?.id)

  useEffect(() => {
    if (transactions && transactions?.length > 0) {
      const hasInvoice = transactions?.some(
        item => item.transactionTypeLabel === 'Invoice' && item.status !== 'CANCELLED',
      )
      setHasInvoiceCheck(hasInvoice)
    }
  }, [transactions, hasInvoiceCheck])

  useEffect(() => {
    if (!propertyData) return
    if (isValidAndNonEmpty(getValues('clientNTE'))) return

    setValue('clientNTE', propertyData?.client?.clientNTE)
  }, [propertyData])
  const {
    fields: tradeFields,
    append: typeFieldAppend,
    remove: typeFieldRemove,
  } = useFieldArray({ control, name: 'tradeAndDescription' })
  const ticketResponse = useTicketId(propertyId, isEdit)

  useEffect(() => {
    if (isValidAndNonEmpty(formValues.ticketId)) return
    setValue('ticketId', ticketResponse.data?.ticketId)
  }, [ticketResponse.isLoading])

  const someFieldsReadOnlyDisabled = formValues?.response1?.value !== 2 && formValues?.response2?.value !== 2

  const saveButtonEnabled = // eslint-disable-line
    formValues.priority?.value &&
    (isEdit ||
      formValues.tradeAndDescription?.every(
        td => isValidAndNonEmpty(td.issueDescription) && td.vendorTrade?.value && td.company?.value,
      )) &&
    isValidAndNonEmpty(formValues.clientNTE) &&
    //isValidAndNonEmpty(formValues.sowOriginalContractAmount) &&
    (someFieldsReadOnlyDisabled || // eslint-disable-line
      formValues.schedule.every(
        s => isValidAndNonEmpty(s.preferredStartDate) && isValidAndNonEmpty(s.preferredStartTime),
      )) && //// eslint-disable-line
    formValues.maintenanceSpecialist?.value && // eslint-disable-line
    formValues.maintenanceCoordinator?.value // eslint-disable-line

  React.useEffect(() => {
    if (!getValues('status')) {
      setValue(
        'status',
        TicketStatusOptions.find(o => o.value === 7),
      )
    }
  }, [])

  const { data: trades } = useTrades()

  const [tradeOptions, setTradeOptions] = useState<SelectOption[]>([])
  useEffect(() => {
    const option = [] as any
    if (trades && trades?.length > 0) {
      trades?.forEach(t => {
        option.push({ label: t.skill as string, value: t.id as number })
      })
    }
    setTradeOptions(option)
  }, [trades])

  const { projectTypeSelectOptions: locations } = useProjectTypeSelectOptions()

  const { isFPM: isMaintenanceSpecialist, isProjectCoordinator: isMaintenanceCoordinator } = useUserRolesSelector()
  const { data } = useAuth()
  const account = data?.user
  const [expiryDate, setExpiryDate] = useState('')

  useEffect(() => {
    if (!isMaintenanceCoordinator) return
    if (formValues?.maintenanceCoordinator?.value) return
    setValue(
      'maintenanceCoordinator',
      maintenanceCoordinatorSelectOptions?.find(m => m.value === account?.id),
    )
  }, [formValues, account])

  useEffect(() => {
    if (projectData?.projectClosedDate) {
      const receivedDate = projectData?.projectClosedDate
      const modifiedDate = new Date(receivedDate)
      modifiedDate.setDate(modifiedDate.getDate() + projectData?.lien_due)
      const formattedDate = modifiedDate.toISOString().split('T')[0]
      setExpiryDate(formattedDate)
      setValue('lienRightExpireDate', expiryDate)
    }
  }, [projectData, expiryDate])

  const isSecondEnabled =
    formValues.clientTextAttempt1 &&
    formValues.clientEmailAttempt1 &&
    formValues.clientLvmAttempt1 &&
    formValues.response1?.value !== 2

  const isSecondAttemptDisabled = !isSecondEnabled || isMaintenanceSpecialist

  const { data: workOrders, isFetching: isLoadingWorkOrders } = useTicketWorkOrders(formValues?.id)

  const isTradeFieldUndefined = !!getValues('tradeAndDescription') // This variable will check if form value of trade and description is undefined or null or empty - HN-PSWOA-5894
  useEffect(() => {
    let tradeFields: any = []

    if (!workOrders || isLoadingWorkOrders) return // Added isLoadingWorkOrders to stop trade and description update - HN-PSWOA-5894

    workOrders?.forEach(workOrder => {
      tradeFields.push({
        id: workOrder.id,
        vendorTrade: tradeOptions?.find(t => t.value === workOrder?.vendorSkillId),
        issueDescription: (workOrder as any)?.assignedItems[0]?.description,
        company: locations?.find(s => s.value?.toString() === (workOrder as any)?.assignedItems[0]?.location),
        isNew: false,
        status: workOrder.status,
      })
    })

    setValue('tradeAndDescription', tradeFields)
  }, [workOrders, locations?.length, tradeOptions?.length, isLoadingWorkOrders, isTradeFieldUndefined]) //  Add isTradeFieldUndefined as dependencies to trigger useEffect to update the trade and description fields of form values - HN-PSWOA-5894
  const disableCancelledOption = workOrders?.map(x => x.statusLabel)

  const projectOverrideStatusSelectOptions = useProjectOverrideStatusSelectOptions(projectData)

  return (
    <>
      <Box pr="10px" data-testid="order-intake-container" marginBottom="100px">
        <Grid templateColumns="repeat(3, 220px)" gap="25px">
          <GridItem>
            <FormControl height="40px" isInvalid={!!errors.ticketId} data-testid="ticketid" zIndex="9">
              <FormLabel fontSize="12px" fontWeight="500" color="gray.600">
                Ticket ID
              </FormLabel>
              <Input
                {...register('ticketId', { required: true })}
                isDisabled={isProjectCancelled || isMaintenanceSpecialist}
                variant="required-field"
                maxLength={50}
              />
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl height="40px" isInvalid={!!errors.priority} data-testid="priority" zIndex="99">
              <FormLabel fontSize="12px" fontWeight={500} color="gray.600">
                Priority
              </FormLabel>
              <Controller
                control={control}
                rules={{ required: 'This is required' }}
                name="priority"
                render={({ field, fieldState }) => {
                  return (
                    <>
                      <Select
                        {...field}
                        options={PriorityOptions}
                        size="md"
                        value={field.value}
                        onChange={option => {
                          field.onChange(option)
                        }}
                        selectProps={{ isBorderLeft: true, menuHeight: '128px' }}
                        isDisabled={isProjectCancelled || isMaintenanceSpecialist}
                      />
                    </>
                  )
                }}
              />
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl height="40px" isInvalid={!!errors.clientNTE} data-testid="clientNTE" zIndex="9">
              <FormLabel fontSize="12px" fontWeight={500} color="gray.600">
                Client NTE
              </FormLabel>

              <Controller
                control={control}
                rules={{ required: true }}
                name="clientNTE"
                render={({ field, fieldState }) => {
                  return (
                    <>
                      <NumberFormat
                        value={field.value}
                        maxLength={20}
                        data-testid="clientNTE-field"
                        thousandSeparator
                        customInput={Input}
                        prefix={'$'}
                        onValueChange={e => {
                          field.onChange(e.floatValue ?? '')
                        }}
                        variant={'required-field'}
                        isDisabled={isProjectCancelled || isMaintenanceSpecialist}
                      />
                    </>
                  )
                }}
              />
              <FormErrorMessage>{errors?.clientNTE?.message}</FormErrorMessage>
            </FormControl>
          </GridItem>
        </Grid>

        <Grid templateColumns="repeat(3, 220px)" gap="25px" mt="60px" marginBottom="50px">
          <GridItem>
            <FormControl
              height="40px"
              isInvalid={!!errors.maintenanceSpecialist}
              data-testid="maintenaceSpecialist"
              zIndex="33"
            >
              <FormLabel fontSize="12px" fontWeight={500} color="gray.600">
                Maintenance Specialist
              </FormLabel>
              <Controller
                control={control}
                name="maintenanceSpecialist"
                render={({ field, fieldState }) => {
                  return (
                    <>
                      <Select
                        {...field}
                        options={maintenanceSpecialistSelectOptions}
                        rules={{ required: true }}
                        size="md"
                        value={field.value}
                        onChange={option => {
                          field.onChange(option)
                        }}
                        selectProps={{ isBorderLeft: true, menuHeight: '135px', overflowY: 'scroll !important' }}
                        isDisabled={isProjectCancelled || isMaintenanceSpecialist}
                      />
                    </>
                  )
                }}
              />
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl
              height="40px"
              isInvalid={!!errors.maintenanceCoordinator}
              data-testid="maintenanceCoordinator"
              zIndex="33"
            >
              <FormLabel fontSize="12px" fontWeight={500} color="gray.600" minW="180px">
                Maintenance Coordinator
              </FormLabel>
              <Controller
                control={control}
                name="maintenanceCoordinator"
                rules={{ required: true }}
                render={({ field, fieldState }) => {
                  return (
                    <>
                      <Select
                        {...field}
                        options={maintenanceCoordinatorSelectOptions}
                        size="md"
                        value={field.value}
                        onChange={option => {
                          field.onChange(option)
                        }}
                        selectProps={{ isBorderLeft: true, menuHeight: '135px' }}
                        isDisabled={isMaintenanceSpecialist || isProjectCancelled}
                      />
                    </>
                  )
                }}
              />
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl
              height="40px"
              isInvalid={!!errors.sowOriginalContractAmount}
              data-testid="sowOriginalContractAmount"
              zIndex="8"
            >
              <FormLabel fontSize="12px" fontWeight={500} color="gray.600">
                Original SOW Amount
              </FormLabel>
              <Controller
                control={control}
                name="sowOriginalContractAmount"
                rules={{ required: false }}
                render={({ field, fieldState }) => {
                  return (
                    <>
                      <NumberFormat
                        value={0.0} //{field.value}
                        data-testid="sowOriginalContractAmount-field"
                        thousandSeparator
                        customInput={Input}
                        prefix={'$'}
                        onValueChange={e => {
                          field.onChange(e.floatValue ?? '')
                        }}
                        // variant={'required-field'}
                        isDisabled={true} //{isMaintenanceSpecialist}
                        maxLength={16}
                      />
                    </>
                  )
                }}
              />
              <FormErrorMessage>{errors?.sowOriginalContractAmount?.message}</FormErrorMessage>
            </FormControl>
          </GridItem>
        </Grid>
        <Grid>
          <GridItem>
            <FormControl w="215px" isInvalid={!!errors.remainingPayment}>
              <FormLabel htmlFor="remainingPayment" variant="strong-label" size="md">
                {t(`project.projectDetails.remainingPayment`)}
              </FormLabel>

              <Controller
                control={control}
                name="remainingPayment"
                render={({ field }) => {
                  return (
                    <NumberInput
                      value={field.value}
                      onChange={e => field.onChange(e.target.value)}
                      disabled={true}
                      customInput={Input}
                      thousandSeparator={true}
                      prefix={'$'}
                    />
                  )
                }}
              />
            </FormControl>
          </GridItem>
        </Grid>

        {tradeFields.map((tradeField: any, index) => {
          return (
            <Grid mt="25px" templateColumns="220px 220px 220px 30px" gap="25px" key={index}>
              <GridItem mt="10px" h="66px">
                <FormControl
                  height="40px"
                  isInvalid={!!errors.tradeFields?.[index]?.vendorTrade}
                  zIndex={`${30 - index}`}
                >
                  <FormLabel fontSize="12px" fontWeight="500" color="gray.600">
                    Trade
                  </FormLabel>
                  <Controller
                    control={control}
                    rules={{ required: tradeField?.status !== STATUS_CODE.CANCELLED }}
                    name={`tradeAndDescription.${index}.vendorTrade`}
                    render={({ field, fieldState }) => {
                      return (
                        <>
                          <Select
                            {...field}
                            options={tradeOptions}
                            size="md"
                            value={field.value}
                            onChange={option => {
                              if (formValues?.tradeAndDescription[index]) {
                                formValues.tradeAndDescription[index].company = null

                                setValue('tradeAndDescription', formValues?.tradeAndDescription)
                              }

                              field.onChange(option)
                            }}
                            selectProps={{ isBorderLeft: tradeField?.status !== STATUS_CODE.CANCELLED }}
                            isDisabled={
                              isMaintenanceSpecialist ||
                              tradeField?.status === STATUS_CODE.CANCELLED ||
                              isProjectCancelled
                            }
                          />
                        </>
                      )
                    }}
                  />
                </FormControl>
              </GridItem>
              <GridItem mt="10px" h="66px">
                <FormControl height="40px" isInvalid={!!errors.tradeFields?.[index]?.company} zIndex={`${30 - index}`}>
                  <FormLabel fontSize="12px" fontWeight="500" color="gray.600">
                    Location
                  </FormLabel>
                  <Controller
                    control={control}
                    rules={{ required: tradeField?.status !== STATUS_CODE.CANCELLED }}
                    name={`tradeAndDescription.${index}.company`}
                    render={({ field, fieldState }) => {
                      return (
                        <>
                          <Select
                            {...field}
                            options={locations}
                            size="md"
                            value={formValues?.tradeAndDescription[index]?.company}
                            onChange={option => {
                              field.onChange(option)
                            }}
                            selectProps={{ isBorderLeft: tradeField?.status !== STATUS_CODE.CANCELLED }}
                            isDisabled={
                              isMaintenanceSpecialist ||
                              tradeField?.status === STATUS_CODE.CANCELLED ||
                              isProjectCancelled
                            }
                          />
                        </>
                      )
                    }}
                  />
                </FormControl>
              </GridItem>
              <GridItem mt="10px">
                <FormControl height="40px" isInvalid={!!errors.tradeFields?.[index]?.issueDescription} zIndex="7">
                  <FormLabel fontSize="12px" fontWeight={500} color="gray.600">
                    Issue Description
                  </FormLabel>
                  <Input
                    id={`tradeAndDescription.${index}.issueDescription`}
                    {...register(`tradeAndDescription.${index}.issueDescription`, {
                      required: tradeField?.status !== STATUS_CODE.CANCELLED,
                    })}
                    placeholder="Enter Issue Description"
                    fontSize="12px"
                    resize="none"
                    variant={tradeField?.status !== STATUS_CODE.CANCELLED ? 'required-field' : 'outline'}
                    isDisabled={
                      isMaintenanceSpecialist || tradeField?.status === STATUS_CODE.CANCELLED || isProjectCancelled
                    }
                    onChange={e => {
                      setValue(`tradeFields.${index}.issueDescription`, (e?.target as HTMLInputElement).value)
                      if ((e.target as HTMLInputElement).value?.length > 65535) {
                        setError(`tradeFields.${index}.issueDescription`, {
                          type: 'custom',
                          message: t(`${NEW_PROJECT}.fieldLimitReached`),
                        })
                      } else {
                        clearErrors(`tradeFields.${index}.issueDescription`)
                      }
                    }}
                  />
                  <FormErrorMessage>{errors?.tradeFields?.[index]?.issueDescription?.message}</FormErrorMessage>
                </FormControl>
              </GridItem>
              {index === 0 && !isMaintenanceSpecialist && (
                <GridItem mt="10px" ml="-15px">
                  <FormControl height="40px" data-testid="startTimeAddField">
                    <FormLabel fontSize="12px" fontWeight={500} color="gray.600">
                      &nbsp;&nbsp;&nbsp;&nbsp;
                    </FormLabel>
                    <HStack>
                      <Button
                        variant="outline"
                        colorScheme="brand"
                        minW="30px"
                        onClick={() => {
                          typeFieldAppend({
                            vendorTrade: null,
                            issueDescription: null,
                            company: null,
                            isNew: true,
                          })
                        }}
                        backgroundColor="#F2F3F4 !important"
                        border="1px solid #CBD5E0 !important"
                        width="24px"
                        height="30px"
                        isDisabled={isProjectCancelled}
                        _hover={{
                          backgroundColor: '#EBF8FF !important',
                        }}
                      >
                        <Icon as={BiPlus} fontSize="20px" />
                      </Button>
                    </HStack>
                  </FormControl>
                </GridItem>
              )}
              {index !== 0 && tradeField.isNew && (
                <GridItem
                  mt="30px"
                  ml="-60px"
                  _hover={{
                    '.delete-row-icon': { visibility: 'visible' },
                  }}
                  position="relative"
                >
                  <Box
                    w="100px"
                    color="#345EA6"
                    fontSize="15px"
                    m={{ base: '4%', md: 0 }}
                    position="relative"
                    top="40%"
                    left="18%"
                  >
                    <Center>
                      <Icon
                        as={MdOutlineCancel}
                        onClick={() => typeFieldRemove(index)}
                        cursor="pointer"
                        position="absolute"
                        boxSize={5}
                        mt="6px"
                        visibility="hidden"
                        className="delete-row-icon"
                      />
                    </Center>
                  </Box>
                </GridItem>
              )}
            </Grid>
          )
        })}

        <HStack
          mt="30px"
          spacing="36px"
          sx={{
            '@media only screen and (max-width: 1400px)': {
              flexDirection: 'column !important',
              alignItems: 'start',
            },
          }}
        >
          <VStack alignItems="start" position="relative" padding="10px" border="1px solid #CBD5E0" borderRadius="4px">
            <FormLabel
              fontSize="12px"
              fontWeight={500}
              color="gray.600"
              mt="6px"
              position="absolute"
              background="#fff"
              zIndex="5"
              top="-16px"
              height="40px"
              width="121px"
              left="20px"
              marginLeft="10px"
              paddingLeft="5px"
            >
              Contact Attempt 1:
            </FormLabel>
            <HStack zIndex="6">
              <Controller
                control={control}
                name="response1"
                render={({ field, fieldState }) => {
                  return (
                    <VStack mt="-10px" position="relative" top="0px">
                      <FormLabel fontSize="12px" fontWeight={500} color="gray.600" m="0" pos="relative" left="-45px">
                        Call
                      </FormLabel>
                      <Box w="120px !important">
                        <Select
                          {...field}
                          options={ContactAttemptOptions}
                          size="md"
                          value={field.value}
                          onChange={option => {
                            field.onChange(option)
                            setIsNotesRequired(true)
                          }}
                          selectProps={{ isBorderLeft: false, menuHeight: '145px' }}
                          isDisabled={isMaintenanceSpecialist || isProjectCancelled}
                        />
                      </Box>
                    </VStack>
                  )
                }}
              />

              <Checkbox
                size="lg"
                marginStart="0.625rem"
                colorScheme="PrimaryCheckBox"
                {...register('clientTextAttempt1')}
                position="relative"
                top="10px"
                isChecked={formValues.clientTextAttempt1}
                isDisabled={isMaintenanceSpecialist || isProjectCancelled}
                onChange={e => {
                  setIsNotesRequired(true)
                  setValue('clientTextAttempt1', e.target.checked)
                }}
              >
                <Text ml="0px" mr="12px" color="gray.600" fontStyle="normal" fontWeight={400} fontSize="12px">
                  Text
                </Text>
              </Checkbox>
              <Checkbox
                size="lg"
                marginStart="0.625rem"
                colorScheme="PrimaryCheckBox"
                {...register('clientEmailAttempt1')}
                mt="-10px !important"
                position="relative"
                top="15px"
                isChecked={formValues.clientEmailAttempt1}
                isDisabled={isMaintenanceSpecialist || isProjectCancelled}
                onChange={e => {
                  setIsNotesRequired(true)
                  setValue('clientEmailAttempt1', e.target.checked)
                }}
              >
                <Text ml="0px" mr="12px" color="gray.600" fontStyle="normal" fontWeight={400} fontSize="12px">
                  Email
                </Text>
              </Checkbox>
              <Checkbox
                size="lg"
                marginStart="0.625rem"
                colorScheme="PrimaryCheckBox"
                {...register('clientLvmAttempt1')}
                mt="-10px !important"
                position="relative"
                top="15px"
                isChecked={formValues.clientLvmAttempt1}
                isDisabled={isMaintenanceSpecialist || isProjectCancelled}
                onChange={e => {
                  setIsNotesRequired(true)
                  setValue('clientLvmAttempt1', e.target.checked)
                }}
              >
                <Text ml="0px" mr="12px" color="gray.600" fontStyle="normal" fontWeight={400} fontSize="12px">
                  LVM
                </Text>
              </Checkbox>
            </HStack>
          </VStack>
          <VStack
            alignItems="start"
            sx={{
              '@media only screen and (max-width: 1400px)': {
                marginInlineStart: '0 !important',
              },
            }}
            position="relative"
            padding="10px"
            border="1px solid #CBD5E0"
            borderRadius="4px"
          >
            <FormLabel
              fontSize="12px"
              fontWeight={500}
              color="gray.600"
              mt="6px"
              position="absolute"
              background="#fff"
              zIndex="5"
              top="-16px"
              height="40px"
              width="121px"
              left="20px"
              marginLeft="10px"
              paddingLeft="5px"
            >
              Contact Attempt 2:
            </FormLabel>
            <HStack zIndex="6">
              <Controller
                control={control}
                name="response2"
                render={({ field, fieldState }) => {
                  return (
                    <VStack mt="-10px" position="relative" top="0px">
                      <FormLabel fontSize="12px" fontWeight={500} color="gray.600" m="0" pos="relative" left="-45px">
                        Call
                      </FormLabel>
                      <Box w="120px !important">
                        <Select
                          {...field}
                          options={ContactAttemptOptions}
                          size="md"
                          value={field.value}
                          onChange={option => {
                            field.onChange(option)
                            setIsNotesRequired(true)
                          }}
                          isDisabled={isSecondAttemptDisabled || isProjectCancelled}
                          selectProps={{ menuHeight: '145px' }}
                        />
                      </Box>
                    </VStack>
                  )
                }}
              />

              <Checkbox
                size="lg"
                marginStart="0.625rem"
                colorScheme="PrimaryCheckBox"
                {...register('clientTextAttempt2')}
                position="relative"
                top="10px"
                isDisabled={!isSecondEnabled || isMaintenanceSpecialist}
                isChecked={formValues.clientTextAttempt2}
                onChange={e => {
                  setIsNotesRequired(true)
                  setValue('clientTextAttempt2', e.target.checked)
                }}
              >
                <Text ml="0px" mr="12px" color="gray.600" fontStyle="normal" fontWeight={400} fontSize="12px">
                  Text
                </Text>
              </Checkbox>
              <Checkbox
                size="lg"
                marginStart="0.625rem"
                colorScheme="PrimaryCheckBox"
                {...register('clientEmailAttempt2')}
                mt="-10px !important"
                position="relative"
                top="15px"
                isDisabled={!isSecondEnabled || isMaintenanceSpecialist}
                isChecked={formValues.clientEmailAttempt2}
                onChange={e => {
                  setIsNotesRequired(true)
                  setValue('clientEmailAttempt2', e.target.checked)
                }}
              >
                <Text ml="0px" mr="12px" color="gray.600" fontStyle="normal" fontWeight={400} fontSize="12px">
                  Email
                </Text>
              </Checkbox>
              <Checkbox
                size="lg"
                marginStart="0.625rem"
                colorScheme="PrimaryCheckBox"
                {...register('clientLvmAttempt2')}
                mt="-10px !important"
                position="relative"
                top="15px"
                isChecked={formValues.clientLvmAttempt2}
                isDisabled={!isSecondEnabled || isMaintenanceSpecialist}
                onChange={e => {
                  setIsNotesRequired(true)
                  setValue('clientLvmAttempt2', e.target.checked)
                }}
              >
                <Text ml="0px" mr="12px" color="gray.600" fontStyle="normal" fontWeight={400} fontSize="12px">
                  LVM
                </Text>
              </Checkbox>
            </HStack>
          </VStack>
        </HStack>

        <HStack alignItems="flex-start" gap="50px">
          <Box>
            {scheduleFields.map((field, index) => {
              return (
                <Grid key={index} templateColumns="repeat(2,170px 170px)" gap="28px" mt="15px" width="360px">
                  <GridItem mt="10px" h="66px">
                    <FormControl
                      height="40px"
                      isInvalid={!!errors.scheduleFields?.[index]?.preferredStartDate}
                      data-testid="preferredStartDate"
                    >
                      <FormLabel fontSize="12px" fontWeight={500} color="gray.600">
                        Preferred Start Date
                      </FormLabel>
                      <Input
                        id={`schedule.${index}.preferredStartDate`}
                        type="date"
                        css={calendarIcon}
                        {...(!someFieldsReadOnlyDisabled ? { variant: 'required-field' } : {})}
                        {...register(`schedule.${index}.preferredStartDate`, {
                          required: !someFieldsReadOnlyDisabled ? true : false,
                        })}
                        isDisabled={someFieldsReadOnlyDisabled || isMaintenanceSpecialist || isProjectCancelled}
                      />
                    </FormControl>
                  </GridItem>
                  <GridItem mt="10px">
                    <FormControl
                      height="40px"
                      isInvalid={!!errors.scheduleFields?.[index]?.preferredStartTime}
                      data-testid="preferredStartTime"
                    >
                      <FormLabel fontSize="12px" fontWeight={500} color="gray.600">
                        Preferred Time
                      </FormLabel>

                      <Controller
                        control={control}
                        name={`schedule.${index}.preferredStartTime`}
                        rules={{ required: !someFieldsReadOnlyDisabled ? true : false }}
                        render={({ field }) => (
                          <>
                            <Select
                              {...field}
                              options={TicketTimeSlots}
                              selected={field.value}
                              onChange={option => field.onChange(option)}
                              selectProps={{
                                isBorderLeft: !someFieldsReadOnlyDisabled ? true : false,
                                menuHeight: '130px',
                              }}
                              isDisabled={someFieldsReadOnlyDisabled || isMaintenanceSpecialist || isProjectCancelled}
                            />
                          </>
                        )}
                      />
                    </FormControl>
                  </GridItem>
                </Grid>
              )
            })}
          </Box>
          <Box>
            <FormControl
              isInvalid={!!errors.clientNotes}
              data-testid="clientNotes"
              mt="50px"
              width="350px"
              height="220px"
              sx={{
                '[disabled]': {
                  opacity: 0.9,
                  cursor: 'not-allowed',
                  background: 'var(--chakra-colors-gray-100);',
                  borderColor: 'var(--chakra-colors-gray-200);',
                  color: 'var(--chakra-colors-gray-400);',
                },
              }}
            >
              <Textarea
                id="clientNotes"
                {...register('clientNotes', { required: isNotesRequired ? 'Notes are required' : false })}
                placeholder="Enter Notes"
                fontSize="12px"
                resize="none"
                height="100%"
                onChange={e => {
                  setValue(`clientNotes`, (e?.target as any).value)
                  if ((e?.target as any).value?.length > 65535) {
                    setError(`clientNotes`, {
                      type: 'custom',
                      message: t(`${NEW_PROJECT}.fieldLimitReached`),
                    })
                  } else {
                    clearErrors(`clientNotes`)
                  }
                }}
                isDisabled={isMaintenanceSpecialist || isProjectCancelled}
              />
              {errors.clientNotes && <FormErrorMessage>{errors.clientNotes.message}</FormErrorMessage>}
            </FormControl>
          </Box>
        </HStack>

        <HStack alignItems="flex-start" gap="50px">
          <Box>
            <Grid templateColumns="repeat(2,170px 170px)" gap="28px" mt="15px" width="360px">
              <GridItem>
                <FormControl height="40px" data-testid="lien_rightExpire_date">
                  <FormLabel fontSize="12px" fontWeight="500" color="gray.600">
                    Lien Expiry date
                  </FormLabel>
                  <Input
                    id="LienExpiray"
                    type="date"
                    {...register('lienRightExpireDate')}
                    css={calendarIcon}
                    isDisabled
                  />
                </FormControl>
              </GridItem>
              <GridItem>
                <FormControl height="40px" data-testid="lien_rightFile_date">
                  <FormLabel fontSize="12px" fontWeight="500" color="gray.600">
                    Lien Filed
                  </FormLabel>
                  <Input {...register('lienRightFileDate')} id="LienFiled" type="date" css={calendarIcon} />
                </FormControl>
              </GridItem>
            </Grid>
          </Box>
        </HStack>

        {isEdit && (
          <HStack alignItems="flex-start" gap="50px">
            <Box>
              <Grid templateColumns="repeat(2,170px 170px)" gap="28px" mt="45px">
                <GridItem>
                  <FormControl height="40px" isInvalid={!!errors.status} data-testid="status">
                    <FormLabel fontSize="12px" fontWeight={500} color="gray.600">
                      Status
                    </FormLabel>
                    <Controller
                      control={control}
                      name="status"
                      render={({ field, fieldState }) => {
                        return (
                          <>
                            <Select
                              {...field}
                              menuPlacement="top"
                              options={TicketStatusOptions}
                              rules={{ required: 'This is required' }}
                              size="md"
                              value={field.value}
                              onChange={option => {
                                field.onChange(option)
                              }}
                              selectProps={{
                                menuHeight: '130px',
                                isBorderLeft: true,
                              }}
                              isDisabled={true} //isMaintenanceSpecialist || isReceivable
                            />
                          </>
                        )
                      }}
                    />
                  </FormControl>
                </GridItem>
                <GridItem>
                  <Box w="170px">
                    <FormControl
                      height="40px"
                      isInvalid={!!errors.overrideProjectStatus}
                      data-testid="override-ticket-status"
                    >
                      <FormLabel fontSize="12px" fontWeight={500} color="gray.600">
                        Override Ticket Status
                      </FormLabel>
                      <Controller
                        control={control}
                        name="overrideProjectStatus"
                        render={({ field, fieldState }) => {
                          return (
                            <>
                              <Select
                                {...field}
                                menuPlacement="top"
                                options={projectOverrideStatusSelectOptions}
                                rules={{ required: false }}
                                size="md"
                                value={field.value}
                                onChange={option => {
                                  if (option.label === 'INVOICED' && !hasInvoiceCheck) {
                                    toast({
                                      title: 'Error',
                                      description: 'The Ticket should have atleast one invoice',
                                      status: 'error',
                                      duration: 9000,
                                      isClosable: true,
                                      position: 'top-left',
                                    })
                                  } else {
                                    field.onChange(option)
                                  }
                                }}
                                isOptionDisabled={option => {
                                  if (
                                    (disableCancelledOption?.includes('Closed') ||
                                      disableCancelledOption?.includes('Invoiced') ||
                                      disableCancelledOption?.includes('Paid')) &&
                                    option.value === 35
                                  ) {
                                    return true
                                  }
                                  return false
                                }}
                                selectProps={{
                                  maxHeight: '150px',
                                  overflowY: 'auto',
                                  isBorderLeft: true,
                                }}
                                isDisabled={isMaintenanceSpecialist || isProjectCancelled || isMaintenanceCoordinator}
                              />
                            </>
                          )
                        }}
                      />
                    </FormControl>
                  </Box>
                </GridItem>
              </Grid>
            </Box>
          </HStack>
        )}
      </Box>

      <Box p={5} borderTop="2px solid #e2e8f0">
        <HStack ml="21px" spacing="16px" w="100%" justifyContent="end">
          {!isMaintenanceSpecialist && (
            <Button
              data-testid="updateBtn"
              colorScheme="brand"
              type="submit"
              isDisabled={!saveButtonEnabled || isProjectCancelled || isLoading}
            >
              Save
            </Button>
          )}
        </HStack>
      </Box>
    </>
  )
}
